import React, { useEffect, useMemo, useState } from "react";
import { Seo, softwareJsonLd, faqJsonLd } from "../components/Seo";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import { toast } from "sonner";
import { TotalReachLogo } from "../components/Logo";
import { PublicNav } from "../components/PublicNav";
import { StickyBookDemo } from "../components/StickyBookDemo";
import { passwordIsStrong } from "./Login";
import { trackEvent } from "../lib/analytics";

import {
  Check, X, ArrowLeft, ArrowRight, Sparkles, Loader2, ShieldCheck,
  Users, Briefcase, Radar, BrainCircuit, Tv, HandCoins,
} from "lucide-react";

const API = process.env.REACT_APP_BACKEND_URL;

// One row per feature we want to expose in the comparison table. `key` is a
// stable id; `label` is what marketing shows; `tiers` maps plan_key → boolean.
// This mirrors backend/services/plans.py FEATURE_MIN_PLAN so the paywall stays
// aligned with what the pricing page promises.
const COMPARISON_ROWS = [
  { section: "Command & Control", features: [
    { key: "dashboard", label: "Command Center dashboard", tiers: { candidate: true, campaign_pro: true, agency: true } },
    { key: "war_room", label: "War Room (live delivery + spend pacing)", tiers: { candidate: true, campaign_pro: true, agency: true } },
    { key: "ask_totalreach", label: "Ask TotalReach — AI Chief of Staff", tiers: { candidate: true, campaign_pro: true, agency: true } },
    { key: "weekly_brief", label: "Weekly Executive Brief (PDF export)", tiers: { candidate: false, campaign_pro: true, agency: true } },
  ]},
  { section: "Advertise", features: [
    { key: "ctv", label: "Connected TV campaigns", tiers: { candidate: true, campaign_pro: true, agency: true } },
    { key: "programmatic", label: "Display, Video & Audio channels", tiers: { candidate: true, campaign_pro: true, agency: true } },
    { key: "email", label: "Email campaigns (Mailgun-powered)", tiers: { candidate: false, campaign_pro: true, agency: true } },
    { key: "direct_mail", label: "Programmatic Direct Mail + QR attribution", tiers: { candidate: false, campaign_pro: true, agency: true } },
    { key: "journeys", label: "Omnichannel Journeys", tiers: { candidate: false, campaign_pro: true, agency: true } },
    { key: "max_campaigns", label: "Active campaigns", tiers: { candidate: "Up to 5", campaign_pro: "Unlimited", agency: "Unlimited" } },
  ]},
  { section: "AI Layer", features: [
    { key: "ai_studio", label: "AI Ad Studio (script + voice + banners + lipsync)", tiers: { candidate: false, campaign_pro: true, agency: true } },
    { key: "ai_refresh", label: "Weekly AI Creative Refresh", tiers: { candidate: false, campaign_pro: true, agency: true } },
    { key: "ai_agent", label: "AI Campaign Agent + Copilot", tiers: { candidate: false, campaign_pro: true, agency: true } },
    { key: "ai_landing", label: "AI Landing Page Builder", tiers: { candidate: false, campaign_pro: true, agency: true } },
  ]},
  { section: "Political Intelligence", features: [
    { key: "intel", label: "Polling averages + momentum trendlines", tiers: { candidate: false, campaign_pro: true, agency: true } },
    { key: "opposition", label: "Opposition Tracker + live ad library", tiers: { candidate: false, campaign_pro: true, agency: true } },
    { key: "pac_alerts", label: "PAC Spend threshold alerts", tiers: { candidate: false, campaign_pro: true, agency: true } },
    { key: "news_digest", label: "Race-scoped news digest", tiers: { candidate: false, campaign_pro: true, agency: true } },
  ]},
  { section: "Fundraising", features: [
    { key: "fundraising_core", label: "Fundraising campaigns + Stripe donations", tiers: { candidate: true, campaign_pro: true, agency: true } },
    { key: "microsites", label: "Microsites + Donor CRM + refunds", tiers: { candidate: true, campaign_pro: true, agency: true } },
    { key: "text_to_give", label: "Text-to-Give SMS keywords", tiers: { candidate: true, campaign_pro: true, agency: true } },
    { key: "receipts", label: "Donor receipts + auto-thanks drip", tiers: { candidate: true, campaign_pro: true, agency: true } },
  ]},
  { section: "Operations", features: [
    { key: "brand_lib", label: "Brand Library (talking points, logo, tone)", tiers: { candidate: false, campaign_pro: true, agency: true } },
    { key: "approvals", label: "Multi-step approval chains", tiers: { candidate: false, campaign_pro: true, agency: true } },
    { key: "compliance", label: "Compliance Center + audit vault", tiers: { candidate: false, campaign_pro: true, agency: true } },
    { key: "voter_files", label: "Voter file onboarding", tiers: { candidate: false, campaign_pro: true, agency: true } },
    { key: "events_volunteers", label: "Events + Volunteers + shift scheduler", tiers: { candidate: true, campaign_pro: true, agency: true } },
    { key: "leaderboard", label: "Volunteer Leaderboard (gamified)", tiers: { candidate: false, campaign_pro: true, agency: true } },
    { key: "qr", label: "QR codes with per-scan attribution", tiers: { candidate: true, campaign_pro: true, agency: true } },
  ]},
  { section: "Agency / Multi-Entity", features: [
    { key: "agency_portfolio", label: "Agency Portfolio (one dashboard, every client)", tiers: { candidate: false, campaign_pro: false, agency: true } },
    { key: "agency_team", label: "Cross-entity team invites", tiers: { candidate: false, campaign_pro: false, agency: true } },
    { key: "unlimited_entities", label: "Unlimited political entities", tiers: { candidate: 1, campaign_pro: 3, agency: "Unlimited" } },
    { key: "custom_markup", label: "Custom media markup & volume pricing", tiers: { candidate: false, campaign_pro: false, agency: true } },
    { key: "sla", label: "Dedicated strategist + SLA", tiers: { candidate: false, campaign_pro: false, agency: true } },
  ]},
];

const TIER_ORDER = ["candidate", "campaign_pro", "agency"];
const TIER_ICONS = { candidate: HandCoins, campaign_pro: BrainCircuit, agency: Briefcase };
const TIER_ACCENTS = {
  candidate: { border: "border-slate-500/30", ring: "", text: "text-slate-200", chip: "bg-slate-800" },
  campaign_pro: { border: "border-cyan-400/40", ring: "ring-2 ring-cyan-400/40", text: "text-cyan-200", chip: "bg-cyan-500/10 text-cyan-300 border-cyan-400/30" },
  agency: { border: "border-fuchsia-400/40", ring: "", text: "text-fuchsia-200", chip: "bg-fuchsia-500/10 text-fuchsia-300 border-fuchsia-400/30" },
};

const FAQ = [
  { q: "Can I switch tiers later?", a: "Yes — upgrade or downgrade any time from Billing. Stripe pro-rates the difference automatically." },
  { q: "Do you have a free trial?", a: "Every plan comes with a 14-day money-back guarantee. If TotalReach isn't right for your race in the first two weeks, we refund your first month." },
  { q: "Are there setup fees?", a: "No setup fees on Candidate or Campaign Pro. Agency plans include a dedicated onboarding session at no extra cost." },
  { q: "What about media spend?", a: "Media spend is separate from your subscription — Candidate uses standard TotalReach media pricing (roughly 50% markup); Agency unlocks volume/custom markup." },
  { q: "How does the SMS keyword feature work?", a: "You publish keywords like GIVE — texters get a personalized donation link back in seconds. Text-to-Give is included on every plan; you supply your own Twilio number." },
  { q: "Do you handle FEC compliance?", a: "Fundraising checkout enforces contribution limits, employer/occupation and attestations. The Compliance Center (Campaign Pro+) adds versioned rules and an Audit Vault." },
];

function priceFor(plan, cycle) {
  if (cycle === "yearly" && plan.price_yearly) {
    return {
      main: `$${Math.round(plan.price_yearly / 12).toLocaleString()}`,
      note: `billed $${Math.round(plan.price_yearly).toLocaleString()}/yr`,
      save: Math.round((1 - plan.price_yearly / (plan.price_monthly * 12)) * 100),
    };
  }
  return { main: plan.price_label || `$${Math.round(plan.price_monthly).toLocaleString()}`, note: null, save: 0 };
}

function BillingToggle({ cycle, setCycle }) {
  return (
    <div className="inline-flex items-center gap-1 rounded-full border border-white/15 bg-white/[0.03] p-1" data-testid="billing-toggle">
      {[["monthly", "Monthly"], ["yearly", "Annual"]].map(([k, label]) => (
        <button key={k} onClick={() => { setCycle(k); trackEvent("pricing_toggle_change", { cycle: k }); }} data-testid={`billing-toggle-${k}`}
          className={`px-4 py-1.5 rounded-full text-sm font-semibold transition-colors ${cycle === k ? "bg-gradient-to-r from-cyan-400 to-blue-500 text-slate-900" : "text-slate-400 hover:text-white"}`}>
          {label}
          {k === "yearly" && (
            <span className={`ml-1.5 text-[10px] font-bold ${cycle === k ? "text-slate-800" : "text-emerald-300"}`}>save 15%+</span>
          )}
        </button>
      ))}
    </div>
  );
}

function TierCard({ plan, cycle, onSelect, submitting }) {
  const key = plan.key;
  const accent = TIER_ACCENTS[key] || TIER_ACCENTS.candidate;
  const Icon = TIER_ICONS[key] || Sparkles;
  const price = priceFor(plan, cycle);
  return (
    <div data-testid={`tier-card-${key}`}
      className={`relative rounded-2xl border ${accent.border} ${accent.ring} bg-white/[0.02] p-6 lg:p-8 flex flex-col h-full`}>
      {plan.highlight && (
        <div className={`absolute -top-3 left-6 text-[10px] uppercase tracking-[0.25em] font-semibold ${accent.chip} border rounded-full px-2.5 py-1`}
          data-testid={`tier-highlight-${key}`}>
          Most popular
        </div>
      )}
      <div className="flex items-center gap-2">
        <Icon className={`w-5 h-5 ${accent.text}`} />
        <h3 className="font-display text-2xl font-bold">{plan.name}</h3>
      </div>
      <p className="text-sm text-slate-400 mt-2 min-h-[42px]">{plan.tagline}</p>
      <div className="mt-6 flex items-baseline gap-2">
        <span className="font-display text-5xl font-bold" data-testid={`tier-price-${key}`}>{price.main}</span>
        <span className="text-slate-500 text-sm">/ month</span>
      </div>
      {price.note ? (
        <div className="mt-1.5 text-xs text-emerald-300" data-testid={`tier-yearly-note-${key}`}>
          {price.note} — save {price.save}%
        </div>
      ) : (
        <div className="mt-1.5 text-xs text-transparent select-none">.</div>
      )}
      <button onClick={() => onSelect(plan)}
        disabled={submitting}
        data-testid={`tier-select-btn-${key}`}
        className={`mt-6 w-full rounded-full py-3 font-semibold text-sm transition-opacity inline-flex items-center justify-center gap-2
        ${key === "campaign_pro"
          ? "bg-gradient-to-r from-cyan-400 to-blue-500 text-slate-900 hover:opacity-90"
          : key === "agency"
          ? "bg-fuchsia-500/10 border border-fuchsia-400/40 text-fuchsia-200 hover:bg-fuchsia-500/20"
          : "bg-white/5 border border-white/15 text-white hover:bg-white/10"} disabled:opacity-50`}>
        {submitting ? <><Loader2 className="w-4 h-4 animate-spin" /> Redirecting…</> : <>Start with {plan.name} <ArrowRight className="w-4 h-4" /></>}
      </button>
      <ul className="mt-8 space-y-3 text-sm">
        {(plan.features || []).map((f, i) => (
          <li key={i} className="flex gap-2.5 text-slate-300 leading-relaxed">
            <Check className={`w-4 h-4 shrink-0 mt-0.5 ${accent.text}`} /> {f}
          </li>
        ))}
      </ul>
    </div>
  );
}

function CellValue({ value }) {
  if (value === true) return <Check className="w-4 h-4 text-emerald-400 mx-auto" data-testid="cell-yes" />;
  if (value === false) return <X className="w-4 h-4 text-slate-600 mx-auto" data-testid="cell-no" />;
  return <span className="text-sm text-slate-200" data-testid="cell-value">{value}</span>;
}

function SignupModal({ plan, cycle, onClose }) {
  const [form, setForm] = useState({ name: "", email: "", password: "", organization: "" });
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState(null);
  const price = priceFor(plan, cycle);

  useEffect(() => {
    trackEvent("signup_modal_open", { plan_key: plan.key, cycle });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const submit = async (e) => {
    e.preventDefault();
    setErr(null);
    if (!passwordIsStrong(form.password)) {
      setErr("Password must have upper + lower case, a number, a symbol, and be at least 8 characters.");
      return;
    }
    setBusy(true);
    trackEvent("signup_submit", { plan_key: plan.key, cycle });
    try {
      const { data } = await axios.post(`${API}/api/public/access/signup-with-plan`, {
        ...form,
        plan_key: plan.key,
        billing_cycle: cycle,
        origin_url: window.location.origin,
      });
      // Redirect to Stripe hosted checkout — Stripe returns to /welcome
      window.location.href = data.checkout_url;
    } catch (e) {
      const detail = e?.response?.data?.detail;
      const message = typeof detail === "string" ? detail : detail?.message || "Could not start checkout.";
      trackEvent("signup_error", { plan_key: plan.key, cycle, message: String(message).slice(0, 120) });
      setErr(message);
      setBusy(false);
    }
  };

  return (
    <div className="fixed inset-0 bg-black/70 z-50 flex items-center justify-center p-4"
      data-testid="pricing-signup-modal" onClick={onClose}>
      <div className="bg-app border border-white/10 rounded-2xl w-full max-w-md p-6"
        onClick={(e) => e.stopPropagation()}>
        <div className="text-[10px] uppercase tracking-[0.25em] text-cyan-300 font-medium">You're subscribing to</div>
        <div className="mt-1 flex items-baseline gap-2">
          <div className="font-display text-2xl font-bold">{plan.name}</div>
          <div className="text-slate-400 text-sm" data-testid="signup-modal-price">
            {price.main}/mo{cycle === "yearly" && price.note ? ` · ${price.note}` : ""}
          </div>
        </div>
        <form onSubmit={submit} className="mt-5 space-y-3" data-testid="pricing-signup-form">
          <div>
            <label className="text-xs text-slate-400">Full name</label>
            <input type="text" required value={form.name}
              onChange={(e) => setForm(f => ({ ...f, name: e.target.value }))}
              data-testid="signup-name"
              className="w-full mt-1 bg-white/[0.03] border border-white/15 rounded-lg px-3 py-2 text-sm focus:border-cyan-400 outline-none" />
          </div>
          <div>
            <label className="text-xs text-slate-400">Work email</label>
            <input type="email" required value={form.email}
              onChange={(e) => setForm(f => ({ ...f, email: e.target.value }))}
              data-testid="signup-email"
              className="w-full mt-1 bg-white/[0.03] border border-white/15 rounded-lg px-3 py-2 text-sm focus:border-cyan-400 outline-none" />
          </div>
          <div>
            <label className="text-xs text-slate-400">Password (8+ chars)</label>
            <input type="password" required minLength={8} value={form.password}
              onChange={(e) => setForm(f => ({ ...f, password: e.target.value }))}
              data-testid="signup-password"
              className="w-full mt-1 bg-white/[0.03] border border-white/15 rounded-lg px-3 py-2 text-sm focus:border-cyan-400 outline-none" />
          </div>
          <div>
            <label className="text-xs text-slate-400">Organization / Campaign (optional)</label>
            <input type="text" value={form.organization}
              onChange={(e) => setForm(f => ({ ...f, organization: e.target.value }))}
              data-testid="signup-organization"
              className="w-full mt-1 bg-white/[0.03] border border-white/15 rounded-lg px-3 py-2 text-sm focus:border-cyan-400 outline-none" />
          </div>
          {err && <div className="text-xs text-rose-300" data-testid="signup-error">{err}</div>}
          <button type="submit" disabled={busy} data-testid="signup-submit-btn"
            className="w-full rounded-full py-3 bg-gradient-to-r from-cyan-400 to-blue-500 text-slate-900 font-semibold text-sm inline-flex items-center justify-center gap-2 disabled:opacity-50">
            {busy ? <><Loader2 className="w-4 h-4 animate-spin" /> Redirecting to Stripe…</> :
              <>Continue to secure payment <ArrowRight className="w-4 h-4" /></>}
          </button>
          <div className="text-[11px] text-slate-500 text-center inline-flex items-center gap-1.5 justify-center w-full">
            <ShieldCheck className="w-3 h-3" /> Payments secured by Stripe. Cancel any time.
          </div>
        </form>
      </div>
    </div>
  );
}

export default function Pricing() {
  const navigate = useNavigate();
  const [plans, setPlans] = useState(null);
  const [err, setErr] = useState(null);
  const [selected, setSelected] = useState(null);
  const [canceled, setCanceled] = useState(false);
  const [cycle, setCycle] = useState("monthly");

  useEffect(() => {
    trackEvent("pricing_page_view");
    axios.get(`${API}/api/public/subscriptions/plans`)
      .then(r => setPlans((r.data || []).filter(p => TIER_ORDER.includes(p.key)).sort((a, b) => TIER_ORDER.indexOf(a.key) - TIER_ORDER.indexOf(b.key))))
      .catch(e => setErr(e?.response?.data?.detail || e?.message || "Could not load plans"));
    const url = new URL(window.location.href);
    if (url.searchParams.get("sub_canceled") === "1") {
      setCanceled(true);
      toast("Checkout canceled — pick a plan to try again.", { icon: "↩️" });
    }
  }, []);

  const planByKey = useMemo(() => {
    const map = {};
    (plans || []).forEach(p => { map[p.key] = p; });
    return map;
  }, [plans]);

  return (
    <div className="min-h-screen bg-app text-white" data-testid="pricing-page">
      <Seo title="Pricing — Candidate, Campaign Pro & Agency plans" description="Transparent monthly and yearly pricing for the TotalReach Political Campaign OS: CTV advertising, FEC-compliant fundraising, race intelligence and AI creative. 14-day money-back guarantee." path="/pricing"
        jsonLd={[softwareJsonLd((plans || []).map((p) => ({ name: p.name, price: Math.round(p.price_monthly || 0), billing: "per month" }))), faqJsonLd(FAQ)]} />
      <PublicNav compact />

      {/* Header */}
      <header className="max-w-6xl mx-auto px-6 pt-14 pb-8 text-center" data-testid="pricing-header">
        <div className="text-xs uppercase tracking-[0.3em] text-cyan-300 font-medium mb-3 inline-flex items-center gap-1.5 justify-center">
          <Sparkles className="w-3 h-3" /> Pricing
        </div>
        <h1 className="font-display text-4xl sm:text-5xl lg:text-6xl font-bold leading-tight max-w-3xl mx-auto">
          One <span className="brand-gradient">operating system</span>. Three ways to run your race.
        </h1>
        <p className="text-slate-400 mt-4 max-w-2xl mx-auto text-base leading-relaxed">
          Every plan includes CTV media, fundraising and the Ask TotalReach AI Chief of Staff. Upgrade when your race needs more firepower.
        </p>
        {canceled && (
          <div className="mt-6 max-w-md mx-auto text-sm text-amber-200 rounded-lg border border-amber-400/30 bg-amber-500/5 p-3"
            data-testid="pricing-canceled-banner">
            Your last checkout was canceled — pick a plan below to try again.
          </div>
        )}
      </header>

      {/* Tier grid */}
      {err && <div className="max-w-6xl mx-auto px-6 py-12 text-rose-200" data-testid="pricing-error">{err}</div>}
      {!plans && !err && (
        <div className="max-w-6xl mx-auto px-6 py-24 flex justify-center">
          <Loader2 className="w-6 h-6 animate-spin text-cyan-300" />
        </div>
      )}
      {plans && (
        <>
          <div className="max-w-6xl mx-auto px-6 pb-8 flex justify-center">
            <BillingToggle cycle={cycle} setCycle={setCycle} />
          </div>
          <section className="max-w-6xl mx-auto px-6 pb-16 grid md:grid-cols-3 gap-6 items-stretch" data-testid="tier-grid">
            {plans.map(p => (
              <TierCard key={p.key} plan={p} cycle={cycle} submitting={false} onSelect={(plan) => {
                trackEvent("tier_select_click", { plan_key: plan.key, cycle, placement: "tier_card" });
                setSelected(plan);
              }} />
            ))}
          </section>
        </>
      )}

      {/* Comparison Table */}
      <section className="border-t border-white/10">
        <div className="max-w-6xl mx-auto px-6 py-16" data-testid="comparison-table">
          <div className="text-center mb-12">
            <div className="text-xs uppercase tracking-[0.3em] text-cyan-300 font-medium mb-2">Compare plans</div>
            <h2 className="font-display text-3xl sm:text-4xl font-bold">Everything that lights up per tier.</h2>
          </div>

          <div className="overflow-x-auto">
            <table className="w-full text-sm border-collapse min-w-[720px]">
              <thead className="sticky top-16 bg-app/95 backdrop-blur">
                <tr>
                  <th className="text-left py-4 pr-4 text-xs uppercase tracking-wider text-slate-500 font-medium">Feature</th>
                  {TIER_ORDER.map(k => {
                    const p = planByKey[k];
                    if (!p) return null;
                    const accent = TIER_ACCENTS[k];
                    return (
                      <th key={k} className={`py-4 px-4 text-center ${accent.text}`} data-testid={`comparison-header-${k}`}>
                        <div className="font-display text-lg font-bold">{p.name}</div>
                        <div className="text-[10px] text-slate-500 mt-1">
                          {priceFor(p, cycle).main}/mo{cycle === "yearly" ? " · annual" : ""}
                        </div>
                      </th>
                    );
                  })}
                </tr>
              </thead>
              <tbody>
                {COMPARISON_ROWS.map((section) => (
                  <React.Fragment key={section.section}>
                    <tr>
                      <td colSpan={4} className="pt-8 pb-2 text-[11px] uppercase tracking-[0.25em] text-cyan-300 font-semibold"
                        data-testid={`compare-section-${section.section.replace(/\s+/g, "-").toLowerCase()}`}>
                        {section.section}
                      </td>
                    </tr>
                    {section.features.map((row) => (
                      <tr key={row.key} className="border-t border-white/5">
                        <td className="py-3 pr-4 text-slate-300" data-testid={`compare-row-${row.key}`}>{row.label}</td>
                        {TIER_ORDER.map(k => (
                          <td key={k} className="py-3 px-4 text-center">
                            <CellValue value={row.tiers[k]} />
                          </td>
                        ))}
                      </tr>
                    ))}
                  </React.Fragment>
                ))}
                <tr className="border-t border-white/5">
                  <td className="py-6"></td>
                  {TIER_ORDER.map(k => {
                    const p = planByKey[k];
                    if (!p) return null;
                    return (
                      <td key={k} className="py-6 px-4 text-center">
                        <button onClick={() => {
                            trackEvent("comparison_cta_click", { plan_key: p.key, cycle });
                            trackEvent("tier_select_click", { plan_key: p.key, cycle, placement: "comparison" });
                            setSelected(p);
                          }}
                          data-testid={`comparison-cta-${k}`}
                          className="text-xs px-4 py-2 rounded-full bg-gradient-to-r from-cyan-400 to-blue-500 text-slate-900 font-semibold inline-flex items-center gap-1.5">
                          Start with {p.name} <ArrowRight className="w-3 h-3" />
                        </button>
                      </td>
                    );
                  })}
                </tr>
              </tbody>
            </table>
          </div>
        </div>
      </section>

      {/* FAQ */}
      <section className="border-t border-white/10">
        <div className="max-w-3xl mx-auto px-6 py-16" data-testid="pricing-faq">
          <div className="text-center mb-10">
            <div className="text-xs uppercase tracking-[0.3em] text-cyan-300 font-medium mb-2">FAQ</div>
            <h2 className="font-display text-3xl sm:text-4xl font-bold">Questions campaign managers ask.</h2>
          </div>
          <div className="space-y-3">
            {FAQ.map((f, i) => (
              <details key={i} className="rounded-xl border border-white/10 bg-white/[0.02] px-5 py-4 open:border-cyan-400/30 group"
                data-testid={`faq-item-${i}`}>
                <summary className="cursor-pointer text-sm font-semibold text-white flex items-center justify-between">
                  {f.q}
                  <span className="text-cyan-300 group-open:rotate-45 transition-transform">+</span>
                </summary>
                <p className="text-sm text-slate-400 mt-3 leading-relaxed">{f.a}</p>
              </details>
            ))}
          </div>
        </div>
      </section>

      {/* Trust strip */}
      <section className="border-t border-white/10">
        <div className="max-w-6xl mx-auto px-6 py-12 grid sm:grid-cols-3 gap-6 text-sm text-slate-400" data-testid="trust-strip">
          <div className="flex gap-3"><ShieldCheck className="w-5 h-5 text-cyan-300 shrink-0" /><div><div className="text-white font-semibold">FEC-ready</div>Contribution limits + attestations baked in.</div></div>
          <div className="flex gap-3"><Radar className="w-5 h-5 text-cyan-300 shrink-0" /><div><div className="text-white font-semibold">Live intelligence</div>Polls, opposition ads, PAC alerts — updated daily.</div></div>
          <div className="flex gap-3"><Users className="w-5 h-5 text-cyan-300 shrink-0" /><div><div className="text-white font-semibold">One team, one login</div>Volunteers, consultants and candidate — all in one workspace.</div></div>
        </div>
      </section>

      {/* Bottom CTA */}
      <section className="border-t border-white/10">
        <div className="max-w-4xl mx-auto px-6 py-16 text-center" data-testid="pricing-bottom-cta">
          <h2 className="font-display text-3xl sm:text-4xl font-bold">Not sure? Talk to us first.</h2>
          <p className="text-slate-400 mt-3">Every campaign is different. Book 20 minutes and we'll map your race to a plan.</p>
          <div className="mt-6 flex items-center justify-center gap-3 flex-wrap">
            <button onClick={() => navigate("/request-demo")} data-testid="pricing-book-demo"
              className="rounded-full px-6 py-3 border border-white/15 text-slate-200 hover:border-cyan-400/50 hover:text-cyan-300">
              Book a demo
            </button>
            <button onClick={() => navigate("/whats-new")} data-testid="pricing-whats-new"
              className="rounded-full px-6 py-3 border border-white/15 text-slate-200 hover:border-cyan-400/50 hover:text-cyan-300 inline-flex items-center gap-1.5">
              <Tv className="w-4 h-4" /> See what's new
            </button>
          </div>
        </div>
      </section>

      <footer className="border-t border-white/10 py-8 text-center text-xs text-slate-600">
        © TotalReach.us · <a href="/" className="hover:text-cyan-300">Home</a> · <a href="/whats-new" className="hover:text-cyan-300">What's New</a>
      </footer>

      {selected && <SignupModal plan={selected} cycle={cycle} onClose={() => setSelected(null)} />}
      <StickyBookDemo placement="pricing" />
    </div>
  );
}
